"use client";

import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/app/_module/components/ui/input";
import { cn } from "@/app/_module/lib/utils";
import EINGuide from "./EINGuide";
import { A2PVerificationData } from "@/app/_module/types/a2p.types";
import { Panel, Section } from "./overview/primitives";
import { Choice, ChoiceGroup, Field, StepIntro, controlClass } from "./overview/wizard-ui";

export default function Step1EINCheck() {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<A2PVerificationData>();

  const hasEIN = watch("hasEIN");

  return (
    <div className="space-y-8">
      <StepIntro title="Your EIN">
        <p>
          Low Volume Standard registers your business by its EIN. You&apos;ll upload the IRS CP 575 letter that
          confirms it in the next step.
        </p>
      </StepIntro>

      <Section id="a2p-has-ein" title="Do you have an EIN for your business?">
        <Controller
          name="hasEIN"
          control={control}
          render={({ field }) => (
            <ChoiceGroup
              aria-labelledby="a2p-has-ein"
              value={field.value ? "yes" : "no"}
              onValueChange={(value) => field.onChange(value === "yes")}
              className="sm:grid-cols-2"
            >
              <Choice value="yes" title="Yes, I have an EIN" />
              <Choice value="no" title="No, I need to get one" />
            </ChoiceGroup>
          )}
        />
      </Section>

      {hasEIN && (
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field
            id="ein"
            label="EIN"
            hint="Nine digits, like 12-3456789."
            error={errors.ein?.message}
          >
            <Controller
              name="ein"
              control={control}
              rules={{
                required: hasEIN ? "Enter your EIN" : false,
                pattern: {
                  value: /^\d{2}-\d{7}$/,
                  message: "Use the format 12-3456789",
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="ein"
                  inputMode="numeric"
                  placeholder="12-3456789"
                  maxLength={10}
                  onChange={(e) => {
                    // Auto-format EIN as user types
                    let value = e.target.value.replace(/\D/g, "");
                    if (value.length > 2) {
                      value = value.slice(0, 2) + "-" + value.slice(2, 9);
                    }
                    field.onChange(value);
                  }}
                  aria-invalid={!!errors.ein}
                  className={cn("h-11 max-w-xs tabular-nums tracking-wide", controlClass, errors.ein && "!border-red-700")}
                />
              )}
            />
          </Field>
          <p className="text-pretty text-sm leading-relaxed text-neutral-600">
            Carriers prefer an EIN that&apos;s at least 45 days old. If yours is newer you can still apply; you&apos;ll
            say so in the next step and our team reviews it by hand.
          </p>
        </Panel>
      )}

      {hasEIN === false && (
        <div className="space-y-3">
          <EINGuide />
          <p className="text-pretty text-sm leading-relaxed text-neutral-500">
            Once you have your EIN, come back and choose &ldquo;Yes, I have an EIN&rdquo; to continue.
          </p>
        </div>
      )}
    </div>
  );
}
